import { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Credits() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newCredits, setNewCredits] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setNewCredits(user.credits.toString());
    setEditDialogOpen(true);
  };

  const handleUpdateCredits = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/users/${selectedUser.id}/credits`,
        { credits: parseFloat(newCredits) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Credits updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update credits');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading credits...</div>;
  }

  return (
    <div data-testid="credits-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Credits Management</h1>
        <p className="text-slate-600">View and manage user credits</p>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              data-testid="credits-search-input"
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="credits-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Current Credits</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`credit-row-${user.id}`}>
                    <td className="py-3 px-4 text-sm text-slate-700">{user.name}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{user.email}</td>
                    <td className="py-3 px-4 text-sm font-semibold text-slate-700">{user.credits.toFixed(2)}</td>
                    <td className="py-3 px-4">
                      <Button
                        data-testid={`edit-credits-button-${user.id}`}
                        size="sm"
                        onClick={() => handleEditClick(user)}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-credits-dialog">
          <DialogHeader>
            <DialogTitle>Edit Credits</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">User: {selectedUser?.name}</label>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">New Credits Amount</label>
              <Input
                data-testid="new-credits-input"
                type="number"
                step="0.01"
                value={newCredits}
                onChange={(e) => setNewCredits(e.target.value)}
                placeholder="Enter new credits"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="cancel-edit-button">
              Cancel
            </Button>
            <Button onClick={handleUpdateCredits} data-testid="save-credits-button" className="bg-purple-600 hover:bg-purple-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
